import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import { FinancialAdminControllerService } from 'src/app/admin-api/api/financialAdminController.service';
import { FinancialControllerService } from 'src/app/admin-api/api/financialController.service';
import { SubscriptionTypeControllerService } from 'src/app/admin-api/api/subscriptionTypeController.service';
import { SubscriptionTypePaymentPriority } from 'src/app/admin-api/model/subscriptionTypePaymentPriority';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-billing-control-payment-priority-modal',
  templateUrl: './billing-control-payment-priority-modal.component.html',
  styleUrls: ['./billing-control-payment-priority-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BillingControlPaymentPriorityModalComponent implements OnInit {
  private financialService = inject(FinancialControllerService);
  private financialAdminService = inject(FinancialAdminControllerService);
  private subscriptionTypeService = inject(SubscriptionTypeControllerService);
  private messageService = inject(MessageService);
  private dialogRef = inject(DynamicDialogRef);

  paymentPriorityList: SubscriptionTypePaymentPriority[] = [];
  subscriptionTypeNameMap = new Map<number, string>();
  subscriptionNameMap = new Map<number, string>();
  isSaving = false;

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    try {
      await this.loadData();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  onPaymentPriorityReordered(list: SubscriptionTypePaymentPriority[]): void {
    this.paymentPriorityList = list;
  }

  getPlanLabel(item: SubscriptionTypePaymentPriority): string {
    const typeId = item.subscriptionTypeId ?? 0;
    const subscriptionName = this.subscriptionNameMap.get(typeId) ?? 'Assinatura';
    const typeName = this.subscriptionTypeNameMap.get(typeId) ?? `Tipo ${typeId}`;
    return `${subscriptionName} — ${typeName}`;
  }

  async savePaymentPriority(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    LoaderService.showLoader();
    try {
      const payload = this.paymentPriorityList.map((item, index) => ({
        ...item,
        priority: index + 1
      }));

      this.paymentPriorityList = await lastValueFrom(
        this.financialAdminService
          .updateSubscriptionTypePaymentPriority(payload)
          .pipe(map((response) => response.result ?? []))
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Salvo',
        detail: 'Prioridade de cobrança por plano atualizada.'
      });
      this.dialogRef.close(true);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.isSaving = false;
      LoaderService.showLoader(false);
    }
  }

  async resetPaymentPriorityOrder(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    LoaderService.showLoader();
    try {
      this.paymentPriorityList = await lastValueFrom(
        this.financialAdminService
          .deleteSubscriptionTypePaymentPriority()
          .pipe(map((response) => response.result ?? []))
      );
      this.sortPaymentPriorityList();
      this.messageService.add({
        severity: 'success',
        summary: 'Redefinido',
        detail: 'Ordem de prioridade de cobrança redefinida.'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.isSaving = false;
      LoaderService.showLoader(false);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private async loadData(): Promise<void> {
    const [priorities, subscriptionGroups] = await Promise.all([
      lastValueFrom(
        this.financialService
          .findSubscriptionTypePaymentPriorityList()
          .pipe(map((response) => response.result ?? []))
      ),
      lastValueFrom(
        this.subscriptionTypeService
          .findAllSubscriptionByTypes()
          .pipe(map((response) => response.result ?? []))
      )
    ]);

    this.subscriptionTypeNameMap = new Map();
    this.subscriptionNameMap = new Map();
    for (const group of subscriptionGroups) {
      for (const type of group.subscriptionTypes ?? []) {
        if (type.subscriptionTypeId == null) {
          continue;
        }
        this.subscriptionTypeNameMap.set(
          type.subscriptionTypeId,
          this.normalizeLabel(type.subscriptionTypeName ?? type.subscriptionTypeId)
        );
        this.subscriptionNameMap.set(
          type.subscriptionTypeId,
          this.normalizeLabel(group.subscriptionName ?? group.subscriptionId)
        );
      }
    }

    this.paymentPriorityList = priorities;
    this.sortPaymentPriorityList();
  }

  private sortPaymentPriorityList(): void {
    this.paymentPriorityList = [...this.paymentPriorityList].sort(
      (left, right) => (left.priority ?? 0) - (right.priority ?? 0)
    );
  }

  private normalizeLabel(value: unknown): string {
    if (value == null) {
      return '';
    }
    return String(value).replace('Anual.', 'Anual/mês');
  }
}
